// Cumulative Season XP required to REACH level i+1 (0-indexed array, levels 1..50).
// Design-provided curve; baked into a literal array (rather than computed at
// runtime) so this file and its client duplicate (client/src/models/season.ts)
// can never drift.
export const SEASON_LEVEL_THRESHOLDS: number[] = [
  50, 104, 161, 223, 289, 360, 437, 519, 606, 701,
  802, 910, 1027, 1152, 1286, 1429, 1584, 1749, 1927, 2118,
  2322, 2541, 2777, 3030, 3301, 3591, 3904, 4238, 4598, 4983,
  5397, 5841, 6317, 6828, 7376, 7964, 8595, 9272, 9999, 10779,
  11615, 12513, 13476, 14509, 15618, 16808, 18084, 19454, 20923, 22500,
];

export const SEASON_MAX_LEVEL = 50;

export function getSeasonLevel(xp: number): number {
  for (let i = SEASON_MAX_LEVEL - 1; i >= 0; i--) {
    if (xp >= SEASON_LEVEL_THRESHOLDS[i]) return i + 1;
  }
  return 0;
}

export type SeasonProgress = {
  level: number;
  xpIntoLevel: number;
  xpForNextLevel: number; // 0 if maxed
  progress: number; // 0..1, 1 if maxed
  isMaxLevel: boolean;
};

export function getSeasonProgress(xp: number): SeasonProgress {
  const level = getSeasonLevel(xp);
  const floor = level > 0 ? SEASON_LEVEL_THRESHOLDS[level - 1] : 0;
  if (level >= SEASON_MAX_LEVEL) {
    return { level, xpIntoLevel: xp - floor, xpForNextLevel: 0, progress: 1, isMaxLevel: true };
  }
  const ceiling = SEASON_LEVEL_THRESHOLDS[level];
  return {
    level,
    xpIntoLevel: xp - floor,
    xpForNextLevel: ceiling - floor,
    progress: (xp - floor) / (ceiling - floor),
    isMaxLevel: false,
  };
}

// Emoticons live as static frames in a single "emoticons" spritesheet (8 cols × 8
// rows, 32×32 per frame — see sprite-loader.ts), addressed by frame-indexed keys
// "emoticon-0".."emoticon-63" so unlock lists (player.data.unlockedEmotes) can just
// store the key string.
export const EMOTICON_COLS = 8;
export const EMOTICON_ROWS = 8;
export const TOTAL_EMOTICON_FRAMES = EMOTICON_COLS * EMOTICON_ROWS;

export const EMOTICON_KEYS: string[] = Array.from(
  { length: TOTAL_EMOTICON_FRAMES },
  (_, i) => `emoticon-${i}`,
);

export function emoticonKeyToFrame(key: string): number {
  return Number(key.slice("emoticon-".length)) || 0;
}

// The first 4 emoticon frames every player starts out with unlocked, before any
// season emote rewards are earned.
export const STARTER_EMOTES: string[] = EMOTICON_KEYS.slice(0, 4);

export type SeasonLootBoxItem = { id: number; qty: number }; // items.json ItemDefinition id + how many to grant

export type SeasonRewardEntry =
  | {
      type: "loot_box";
      tier: "bronze" | "silver" | "gold" | "platinum"; // cosmetic only now (chest sprite/label color)
      credits: number;
      items: SeasonLootBoxItem[];
    }
  | { type: "credits"; amount: number }
  | { type: "spray"; id: number }
  | { type: "emote"; id: number }
  | { type: "profile_icon"; id: number }
  | { type: "title"; id: number }
  | { type: "border"; id: number }
  | { type: "hat_skin"; id: number } // matches hats.json ids
  | { type: "player_skin"; skinId: string }; // skinId matches a season-skins.json id

export type SeasonLevelRow = {
  level: number;
  free: SeasonRewardEntry;
  premium: SeasonRewardEntry;
};
